// The app's data, generated from .appfactory/product-plan.json.
//
// Regenerated by `appfactory product apply`. Edit the PLAN and re-apply
// rather than editing here, or the next apply will disagree with you.
//
// Deliberately the same shape the chassis shipped: plain `ref`s in a module,
// seeded through `demoData` so /demo shows rows and the real app starts
// empty. A generated app should not introduce a state library its author
// never asked for — and `demoData` is what makes the QA `empty` fixture
// actually produce an empty screen rather than a populated one.

import { ref, computed, watch, toRaw } from 'vue';
import { demoData } from './core/demo.js';
import { records } from './core/storage.js';

// Rows survive a reload.
//
// `core/storage.js` is an IndexedDB record store the chassis has always
// shipped, and the generated store used to ignore it completely — a plain
// `ref([])` that lost everything the moment the page reloaded. An app that
// cannot keep what you typed into it is not working, however well it renders.
//
// Demo rows are NOT persisted. They have negative ids and exist for /demo and
// for QA fixtures; writing them to the real store would put sample customers
// in somebody's actual queue.
const persist = (row) => (Number(row?.id) > 0 ? records.put(toRaw(row)) : null);

// ONE id sequence for every entity, because there is one store.
//
// `records` is a single IndexedDB object store keyed on `id`. Giving each
// entity its own counter starting at 1 meant the first order written
// overwrote the first customer, and removeOrder(1) deleted them — silent
// cross-entity data loss that only appears once an app has two entities.
//
// Seeded from the highest id already stored, so it also survives a reload:
// restarting at 1 would overwrite the first row ever added.
let seq = 0;
const nextId = () => (seq += 1);

// Everything hydrates from ONE read, and nothing may allocate an id before
// it finishes. A row added while this was still in flight took id 1,
// overwrote a stored record, and was then dropped from the screen when the
// hydrated rows replaced the list.
const ready = records.all().then((rows) => {
  seq = rows.reduce((n, r) => Math.max(n, Number(r.id) || 0), 0);
  return rows;
}).catch(() => []);   // no IndexedDB (SSR, private mode) — stay in memory

/** Resolves once stored rows are loaded. Await it before writing. */
export const storeReady = ready;

/** One item in the shipped question bank. `payload` carries the mode-specific shape: two comparables for Higher or Lower, four items plus the reason for Odd One Out, an ordered list for Sort It, a clue ladder for Guess the Thing. */
export const questions = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededQuestion = demoData([
  { id: -1, mode: 'chain-reaction', topic: 'football', difficulty: 'warm-up', prompt: 'A short prompt for Question 1, long enough to show wrapping.', payload: 'A short payload for Question 1, long enough to show wrapping.', answer: 'Sample answer', reason: 'A short reason for Question 1, long enough to show wrapping.', ausBonus: true },
  { id: -2, mode: 'higher-lower', topic: 'animals', difficulty: 'sweaty', prompt: 'A short prompt for Question 2, long enough to show wrapping.', payload: 'A short payload for Question 2, long enough to show wrapping.', answer: 'Sample answer', reason: 'A short reason for Question 2, long enough to show wrapping.', ausBonus: false },
  { id: -3, mode: 'odd-one-out', topic: 'geography', difficulty: 'fully-austinated', prompt: 'A short prompt for Question 3, long enough to show wrapping.', payload: 'A short payload for Question 3, long enough to show wrapping.', answer: 'Sample answer', reason: 'A short reason for Question 3, long enough to show wrapping.', ausBonus: false },
]);
watch(seededQuestion, (rows) => {
  if (rows.length && !questions.value.length) questions.value = [...rows];
}, { immediate: true });

export const questionsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'question');
  if (mine.length) questions.value = mine;
  return questions.value;
});

export function addQuestion(input = {}) {
  const row = { id: nextId(), entity: 'question',
    mode: input.mode ?? '',
    topic: input.topic ?? '',
    difficulty: input.difficulty ?? '',
    prompt: input.prompt ?? '',
    payload: input.payload ?? '',
    answer: input.answer ?? '',
    reason: input.reason ?? '',
    ausBonus: input.ausBonus ?? false,
  };
  questions.value = [row, ...questions.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveQuestion(row) {
  questions.value = questions.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeQuestion(id) {
  questions.value = questions.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findQuestion = (id) =>
  questions.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentQuestion = computed(() => questions.value.slice(0, 3));
export const questionsTitle = (row) => row?.answer;

/** One played session of any mode, including Endurance and the Daily. */
export const runs = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededRun = demoData([
  { id: -1, mode: 'chain-reaction', score: 1, lastedMs: 1, bestChain: 1, correct: 1, wrong: 1, ausBonuses: 1, endedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString() },
  { id: -2, mode: 'higher-lower', score: 2, lastedMs: 2, bestChain: 2, correct: 2, wrong: 2, ausBonuses: 2, endedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString() },
  { id: -3, mode: 'odd-one-out', score: 3, lastedMs: 3, bestChain: 3, correct: 3, wrong: 3, ausBonuses: 3, endedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString() },
]);
watch(seededRun, (rows) => {
  if (rows.length && !runs.value.length) runs.value = [...rows];
}, { immediate: true });

export const runsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'run');
  if (mine.length) runs.value = mine;
  return runs.value;
});

export function addRun(input = {}) {
  const row = { id: nextId(), entity: 'run',
    mode: input.mode ?? '',
    score: input.score ?? 0,
    lastedMs: input.lastedMs ?? 0,
    bestChain: input.bestChain ?? 0,
    correct: input.correct ?? 0,
    wrong: input.wrong ?? 0,
    ausBonuses: input.ausBonuses ?? 0,
    endedAt: input.endedAt ?? new Date().toISOString(),
  };
  runs.value = [row, ...runs.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveRun(row) {
  runs.value = runs.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeRun(id) {
  runs.value = runs.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findRun = (id) =>
  runs.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentRun = computed(() => runs.value.slice(0, 3));
export const runsTitle = (row) => row?.id;

/** A badge in Austin's Trophy Room. Ships locked; earnedAt is set the moment its criterion is met. */
export const trophys = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededTrophy = demoData([
  { id: -1, name: 'Trophy 1', description: 'A short description for Trophy 1, long enough to show wrapping.', criterion: 'Sample criterion', icon: 'Sample icon', earnedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString() },
  { id: -2, name: 'Trophy 2', description: 'A short description for Trophy 2, long enough to show wrapping.', criterion: 'Sample criterion', icon: 'Sample icon', earnedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString() },
  { id: -3, name: 'Trophy 3', description: 'A short description for Trophy 3, long enough to show wrapping.', criterion: 'Sample criterion', icon: 'Sample icon', earnedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString() },
]);
watch(seededTrophy, (rows) => {
  if (rows.length && !trophys.value.length) trophys.value = [...rows];
}, { immediate: true });

export const trophysReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'trophy');
  if (mine.length) trophys.value = mine;
  return trophys.value;
});

export function addTrophy(input = {}) {
  const row = { id: nextId(), entity: 'trophy',
    name: input.name ?? '',
    description: input.description ?? '',
    criterion: input.criterion ?? '',
    icon: input.icon ?? '',
    earnedAt: input.earnedAt ?? new Date().toISOString(),
  };
  trophys.value = [row, ...trophys.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveTrophy(row) {
  trophys.value = trophys.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeTrophy(id) {
  trophys.value = trophys.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findTrophy = (id) =>
  trophys.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentTrophy = computed(() => trophys.value.slice(0, 3));
export const trophysTitle = (row) => row?.name;

/** The single local record. One shared total across every mode, so every game feeds the same number. Never leaves the device. */
export const profiles = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededProfile = demoData([
  { id: -1, totalScore: 1, bestEnduranceMs: 1, dailyStreak: 1, lastDailyOn: '2026-01-01', topics: 'A short topics for Profile 1, long enough to show wrapping.', difficulty: 'warm-up', sound: true, haptics: true },
  { id: -2, totalScore: 2, bestEnduranceMs: 2, dailyStreak: 2, lastDailyOn: '2026-01-02', topics: 'A short topics for Profile 2, long enough to show wrapping.', difficulty: 'sweaty', sound: false, haptics: false },
  { id: -3, totalScore: 3, bestEnduranceMs: 3, dailyStreak: 3, lastDailyOn: '2026-01-03', topics: 'A short topics for Profile 3, long enough to show wrapping.', difficulty: 'fully-austinated', sound: false, haptics: false },
]);
watch(seededProfile, (rows) => {
  if (rows.length && !profiles.value.length) profiles.value = [...rows];
}, { immediate: true });

export const profilesReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'profile');
  if (mine.length) profiles.value = mine;
  return profiles.value;
});

export function addProfile(input = {}) {
  const row = { id: nextId(), entity: 'profile',
    totalScore: input.totalScore ?? 0,
    bestEnduranceMs: input.bestEnduranceMs ?? 0,
    dailyStreak: input.dailyStreak ?? 0,
    lastDailyOn: input.lastDailyOn ?? new Date().toISOString(),
    topics: input.topics ?? '',
    difficulty: input.difficulty ?? '',
    sound: input.sound ?? false,
    haptics: input.haptics ?? false,
    // These two are the clock settings (see game/pace.js). They have to be
    // named here: this builds the row from an explicit field list, so anything
    // missing is dropped on write without an error, and the default in
    // game/profile.js would never reach storage.
    timed: input.timed ?? true,
    timeScale: input.timeScale ?? 1,
    regenLives: input.regenLives ?? false,
  };
  profiles.value = [row, ...profiles.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveProfile(row) {
  profiles.value = profiles.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeProfile(id) {
  profiles.value = profiles.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findProfile = (id) =>
  profiles.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentProfile = computed(() => profiles.value.slice(0, 3));
export const profilesTitle = (row) => row?.id;

/** One puzzle a day, one attempt, pulled from any mode. Chosen deterministically from the date so it is the same all day and cannot be re-rolled by reopening the app. */
export const dailyPuzzles = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededDailyPuzzle = demoData([
  { id: -1, onDate: '2026-01-01', mode: 'chain-reaction', question: -1, attempted: true, outcome: 'pending' },
  { id: -2, onDate: '2026-01-02', mode: 'higher-lower', question: -2, attempted: false, outcome: 'survived' },
  { id: -3, onDate: '2026-01-03', mode: 'odd-one-out', question: -3, attempted: false, outcome: 'exhausted' },
]);
watch(seededDailyPuzzle, (rows) => {
  if (rows.length && !dailyPuzzles.value.length) dailyPuzzles.value = [...rows];
}, { immediate: true });

export const dailyPuzzlesReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'daily-puzzle');
  if (mine.length) dailyPuzzles.value = mine;
  return dailyPuzzles.value;
});

export function addDailyPuzzle(input = {}) {
  const row = { id: nextId(), entity: 'daily-puzzle',
    onDate: input.onDate ?? new Date().toISOString(),
    mode: input.mode ?? '',
    question: input.question ?? '',
    attempted: input.attempted ?? false,
    outcome: input.outcome ?? '',
  };
  dailyPuzzles.value = [row, ...dailyPuzzles.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveDailyPuzzle(row) {
  dailyPuzzles.value = dailyPuzzles.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeDailyPuzzle(id) {
  dailyPuzzles.value = dailyPuzzles.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findDailyPuzzle = (id) =>
  dailyPuzzles.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentDailyPuzzle = computed(() => dailyPuzzles.value.slice(0, 3));
export const dailyPuzzlesTitle = (row) => row?.id;
